<script setup lang="ts">
/*
 * FILE: unified_ui/src/components/BottomNav.vue
 * PURPOSE: Mobile bottom tab bar — iOS 26 liquid glass floating pill.
 *          3 tabs: Заказы (home), Чат, О нас. Hidden on md+ (desktop uses TopNav).
 *          Also hidden on /new-order (wizard) and /orders/:id (order detail) —
 *          those are full-screen flows and need every pixel.
 */
import { computed } from 'vue'
import { useRoute, RouterLink } from 'vue-router'

const tabs = [
  { to: '/orders', label: 'КП', icon: 'orders' },
  { to: '/chat',   label: 'Чат',    icon: 'chat' },
  { to: '/about',  label: 'О нас',  icon: 'about' },
]

const route = useRoute()

function isActive(to: string) {
  return route.path === to || route.path.startsWith(to + '/')
}

/**
 * Hide the floating pill on full-screen flows:
 *  - /new-order — wizard takes the whole screen
 *  - /orders/:id — order detail is a full-screen view
 *  Top-level /orders still shows the pill.
 */
const isVisible = computed(() => {
  if (route.path === '/new-order') return false
  if (route.path.startsWith('/orders/')) return false
  return true
})
</script>

<template>
  <div
    v-if="isVisible"
    class="fixed inset-x-0 bottom-0 z-40 pb-safe md:hidden pointer-events-none"
  >
    <div class="pointer-events-auto mx-auto max-w-md px-4 pb-3">
      <nav class="nav-pill flex items-center justify-around px-2 py-1.5">
        <RouterLink
          v-for="tab in tabs"
          :key="tab.to"
          :to="tab.to"
          class="flex flex-col items-center gap-0.5 rounded-full px-3 py-1.5 transition-all duration-200"
          :class="isActive(tab.to)
            ? 'bg-white text-brand-500 shadow-sm dark:bg-slate-700 dark:text-brand-400'
            : 'text-ink-500 dark:text-slate-400 active:text-ink-700 dark:active:text-slate-200'"
        >
          <!-- Orders icon -->
          <svg v-if="tab.icon === 'orders'" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1.5" />
            <rect x="14" y="3" width="7" height="7" rx="1.5" />
            <rect x="3" y="14" width="7" height="7" rx="1.5" />
            <rect x="14" y="14" width="7" height="7" rx="1.5" />
          </svg>
          <!-- Chat icon -->
          <svg v-else-if="tab.icon === 'chat'" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
          <!-- About icon -->
          <svg v-else class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <span class="text-[10px] font-semibold leading-none">{{ tab.label }}</span>
        </RouterLink>
      </nav>
    </div>
  </div>
</template>
